import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, animate } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine, EASE } from "./motion";
import { OFFER, OPTION, IMAGES, fmt } from "./data";

const Counter = ({ target }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const c = animate(0, target, {
      duration: 2,
      delay: 1.4,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => c.stop();
  }, [target]);
  return <span data-testid="hero-amount">{fmt(val)}</span>;
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative isolate flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img src={IMAGES.hero} alt="Oilfield pumpjack at sunset" className="h-[115%] w-full object-cover" />
        <div className="absolute inset-0 bg-[#0b0f17]/55" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#0b0f17] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="mx-auto w-full max-w-7xl px-4 pt-36 pb-16 sm:px-6 lg:px-8">
        <MaskedLine delay={0.2}>
          <p className="font-mono2 mb-6 flex items-center gap-3 text-xs tracking-[0.35em] text-amber-500 uppercase">
            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-amber-500" />
            Commercial Offer · {OFFER.date}
          </p>
        </MaskedLine>

        <h1 className="font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-7xl">
          <MaskedLine delay={0.35}>Odoo ERP</MaskedLine>
          <MaskedLine delay={0.5}>Implementation</MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="text-amber-500">Services</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.85} className="mt-8">
          <p className="max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
            Prepared for <span className="font-semibold text-white">{OFFER.client}</span> — consolidating
            four-country operations (UAE · KSA · Qatar · Egypt) onto one integrated platform.{" "}
            Manufacturing, rentals, field service and finance, delivered in 6–7 months by{" "}
            <span className="font-semibold text-white">{OFFER.consultant}</span>.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
          className="mt-12 flex flex-wrap items-end gap-x-14 gap-y-8 border-t border-white/15 pt-8"
        >
          <div>
            <p className="font-mono2 mb-2 text-[11px] tracking-[0.3em] text-slate-400 uppercase">
              Consultancy Engagement · No VAT
            </p>
            <p className="font-display text-4xl font-bold text-white sm:text-5xl">
              <span className="mr-2 text-xl font-medium text-amber-500 sm:text-2xl">AED</span>
              <Counter target={OPTION.base} />
            </p>
            <p className="mt-2 text-sm text-slate-400">
              Special rate — reduced from <span className="line-through">AED {fmt(OPTION.originalBase)}</span>
            </p>
            <p className="mt-1 text-xs font-medium text-amber-500/90">
              Implementation services only — Odoo license & hosting are billed separately, paid directly to Odoo
            </p>
          </div>
          <div className="pb-1">
            <p className="font-mono2 mb-2 text-[11px] tracking-[0.3em] text-slate-400 uppercase">Valid Until</p>
            <p className="font-display text-xl font-semibold text-slate-200">{OFFER.validUntil}</p>
          </div>
          <button
            data-testid="hero-view-options-button"
            onClick={() => document.getElementById("investment")?.scrollIntoView({ behavior: "smooth" })}
            className="print-hidden mb-1 rounded-full bg-amber-500 px-7 py-3 text-xs font-bold tracking-widest text-[#0b0f17] uppercase transition-[background-color,transform] duration-300 hover:bg-amber-400 active:scale-95"
          >
            View investment
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="print-hidden absolute bottom-6 left-1/2 -translate-x-1/2"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-amber-500/70" />
      </motion.div>
    </section>
  );
};
